import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";

// NOTES - the appendix's methodology/assumptions/treatment notes: standing boilerplate
// documenting HOW the report's figures are computed, not report-specific facts (those live in
// the exec narrative / driver table / data-quality summary elsewhere on the report). Matches
// the approved v2 mockup's two-column appendix layout (.superpowers/brainstorm/**/
// report-redesign-v2.html) plus the two extra notes the Task-17 brief calls for that the
// mockup folded into "Forecast methodology" prose (confidence & range, provider invoice
// timing). Static copy per YAGNI - none of this varies per report run today, so there's
// nothing to fetch or inject; a future run-varying note can read off `narrative`/`binding`
// the same way NotesConfidenceBlock/NotesBlock already do, without restructuring this list.
const NOTES = [
  {
    label: "Forecast methodology",
    text: "Ensemble model selection (Theta / TiDE) with conformal-calibrated intervals and rolling-origin validation.",
  },
  {
    label: "Confidence & range",
    text: "The forecast range shown is a 90% calibrated interval; confidence improves as more of the period's actuals are observed.",
  },
  {
    label: "Key assumptions",
    text: "No material new products or pricing changes assumed within the horizon; usage growth continues at the trailing-quarter rate.",
  },
  {
    label: "Allocation coverage",
    text: "Spend is allocated to accountable owners via the cost-ownership hierarchy; unallocated spend is shown separately in the data-quality summary.",
  },
  {
    label: "Currency & FX",
    text: "All figures are reported in EUR. Non-EUR provider invoices are converted at the billing-period rate.",
  },
  {
    label: "Commitment & discount treatment",
    text: "Committed-use and reserved-capacity discounts are applied at the SKU level; figures are shown net of discount.",
  },
  {
    label: "Provider invoice timing",
    text: "Provider invoices typically finalize 2-3 days after the billing period closes; late-arriving charges are trued up in the following cycle.",
  },
  {
    label: "AI subscription allocation",
    text: "Flat-fee AI subscription cost is allocated by usage (token) share; the pay-as-you-go equivalent is shown alongside for reference.",
  },
  {
    label: "Late / estimated data",
    text: "Recent days may include estimated cost pending provider invoice finalization; estimated amounts are flagged in the data-quality summary.",
  },
  {
    label: "Material one-off adjustments",
    text: "None recorded for this period unless noted in the executive summary or driver table above.",
  },
];

// Split into two roughly-even columns, left-to-right then top-to-bottom (matching the
// mockup's grid), rather than interleaving - so a reader scans one whole column's topics
// before moving to the next.
const MID = Math.ceil(NOTES.length / 2);
const COLUMNS = [NOTES.slice(0, MID), NOTES.slice(MID)];

/**
 * AppendixNotesBlock - the appendix's methodology/assumptions/treatment notes, two-column
 * (appendix_notes block type).
 *
 * NOT a fetching block: no run-varying data source exists for these notes today - see NOTES
 * above (YAGNI, mirrors NotesBlock/ExecNarrativeBlock/DriverTableBlock's prop-injected,
 * non-fetching pattern for board-report content that IS run-specific).
 */
const AppendixNotesBlock = ({ title = "Notes & assumptions" }) => (
  <BaseBlock title={title} isLoading={false} error={null} className="a4-appendix-notes">
    <div className="a4-appendix-notes-grid">
      {COLUMNS.map((col, colIndex) => (
        // eslint-disable-next-line react/no-array-index-key -- static, fixed-length columns
        <div key={colIndex} className="a4-appendix-notes-col">
          {col.map((note) => (
            <div key={note.label} className="a4-appendix-note">
              <h4>{note.label}</h4>
              <p>{note.text}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  </BaseBlock>
);

AppendixNotesBlock.propTypes = {
  title: PropTypes.string,
};

export default AppendixNotesBlock;
